import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiArrowUpRight } from "react-icons/fi";

import { AppWrap, MotionWrap } from "../../wrapper";
import { client, urlFor } from "../../client";
import { images } from "../../constants";
import "./Work.scss";

const FILTERS = [
  { id: "all", label: "All", aliases: ["all"] },
  { id: "react", label: "React JS", aliases: ["React JS", "ReactJS", "React"] },
  { id: "javascript", label: "JavaScript", aliases: ["JavaScript", "JS", "Vanilla JS"] },
  { id: "html-css", label: "HTML/CSS", aliases: ["HTML/CSS", "HTML CSS", "HTML", "CSS"] },
];

const fallbackWorks = [
  {
    title: "Premium Portfolio System",
    description: "A modern personal portfolio with responsive sections, clean content flow, and a polished product-company visual language.",
    tags: ["React JS", "JavaScript"],
    projectLink: "#contact",
    localImage: images.about01,
  },
  {
    title: "Launch-Focused Landing Page",
    description: "A conversion-oriented landing page with clear messaging, compact feature blocks, and high-performance styling.",
    tags: ["HTML/CSS", "JavaScript"],
    projectLink: "#contact",
    localImage: images.about02,
  },
  {
    title: "Frontend Delivery Dashboard",
    description: "A reusable UI foundation for tracking work, presenting status, and keeping stakeholders aligned through delivery.",
    tags: ["React JS"],
    projectLink: "#contact",
    localImage: images.about03,
  },
];

const resolveImage = (work) => {
  if (work.localImage) return work.localImage;
  if (work.imgUrl) return urlFor(work.imgUrl);
  return images.about04;
};

const normalizeTag = (value = "") =>
  value.toString().toLowerCase().replace(/[^a-z0-9]/g, "");

const TOOL_LANGUAGE_RE = /\b(react|reactjs|react js|javascript|js|typescript|html|css|html\/css|redux|sanity|cms|node|express|mongodb|mongo)\b/i;

const workHasFilterTag = (work, filter) => {
  if (filter.id === "all") return true;
  const aliases = filter.aliases.map(normalizeTag).filter(Boolean);
  const searchable = [
    work.title,
    work.description,
    ...(work.tags || []),
  ].map(normalizeTag).filter(Boolean);

  return aliases.some((alias) =>
    searchable.some((value) => value.includes(alias) || alias.includes(value))
  );
};

const getWorkCategory = (work) => {
  const searchable = [
    work.title,
    work.description,
    ...(work.tags || []),
  ].map(normalizeTag).filter(Boolean);

  const has = (...keywords) =>
    keywords.map(normalizeTag).some((keyword) =>
      searchable.some((value) => value.includes(keyword))
    );

  if (has("dashboard", "admin", "operations")) return "Dashboard";
  if (has("landing", "website", "web experience")) return "Website";
  if (has("portfolio")) return "Portfolio";
  return "Frontend";
};

const getDisplayTags = (work) => {
  const category = getWorkCategory(work);
  const secondary =
    category === "Dashboard"
      ? "Interface System"
      : category === "Website"
        ? "Web Experience"
        : category === "Portfolio"
          ? "Responsive UI"
          : "UI Engineering";

  return [...new Set([category, secondary])];
};

const getWorkDescription = (work) => {
  const description = work.description || "";
  if (description && !TOOL_LANGUAGE_RE.test(description)) return description;

  const category = getWorkCategory(work);
  const title = normalizeTag(work.title);

  if (title.includes("shop") || title.includes("commerce")) {
    return "Responsive ecommerce interface focused on clear shopping flows, strong product presentation, and smooth browsing.";
  }

  if (title.includes("photo") || title.includes("snapshot")) {
    return "Visual portfolio interface with clean navigation, immersive imagery, and responsive presentation.";
  }

  if (category === "Dashboard") {
    return "Operational interface built around readable status, clear hierarchy, and dependable day-to-day workflows.";
  }

  if (category === "Website" || category === "Portfolio") {
    return "Responsive web experience with clear messaging, polished visuals, and stable layout behavior across screens.";
  }

  return "Frontend interface work focused on responsive layout, clear interaction, and polished delivery.";
};

const Work = () => {
  const [works, setWorks] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    client
      .fetch('*[_type == "works"]')
      .then((data) => setWorks(data || []))
      .catch(() => setWorks([]));
  }, []);

  const sourceWorks = works.length ? works : fallbackWorks;
  const activeFilterData = FILTERS.find((item) => item.id === activeFilter) || FILTERS[0];

  const filteredWorks = useMemo(() => {
    return sourceWorks.filter((work) => workHasFilterTag(work, activeFilterData));
  }, [activeFilterData, sourceWorks]);

  return (
    <div className="wrk">
      <div className="app__section-head">
        <motion.div
          className="app__section-title"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          <span className="app__kicker">
            <em>02</em>Selected work
          </span>
          <h2>
            Projects built for <em>daily use.</em>
          </h2>
        </motion.div>

        <motion.p
          className="app__section-lede"
          initial={{ opacity: 0, y: 26 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true }}
        >
          Landing pages, dashboards, and interface systems shaped around
          clarity, motion, and maintainability.
        </motion.p>
      </div>

      <motion.div
        className="wrk__filters"
        role="group"
        aria-label="Project filters"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        viewport={{ once: true }}
      >
        <span className="wrk__filters-label">Filter</span>
        {FILTERS.map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setActiveFilter(item.id)}
            className={`wrk__filter${activeFilter === item.id ? " is-active" : ""}`}
            aria-pressed={activeFilter === item.id}
          >
            {item.label}
          </button>
        ))}
        <span className="wrk__count">
          {String(filteredWorks.length).padStart(2, "0")} projects
        </span>
      </motion.div>

      <motion.div layout className="wrk__grid">
        <AnimatePresence mode="popLayout">
          {filteredWorks.map((work, index) => {
            const displayTags = getDisplayTags(work);
            const external = work.projectLink?.startsWith("http");

            return (
              <motion.article
                layout
                className="wrk__card"
                key={`${work.title}-${index}`}
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.97 }}
                transition={{ duration: 0.5, delay: index * 0.07, ease: [0.22, 1, 0.36, 1] }}
                viewport={{ once: true, amount: 0.12 }}
              >
                <a
                  href={work.projectLink || "#contact"}
                  target={external ? "_blank" : undefined}
                  rel={external ? "noreferrer" : undefined}
                  className="wrk__media"
                  aria-label={`Open ${work.title}`}
                  tabIndex={-1}
                >
                  <span className="wrk__media-bar">
                    <i />
                    <i />
                    <i />
                    <strong>{displayTags[0]}</strong>
                  </span>
                  <span className="wrk__shot">
                    <img src={resolveImage(work)} alt={work.title} loading="lazy" />
                  </span>
                </a>

                <div className="wrk__body">
                  <div className="wrk__meta">
                    <span>Case {String(index + 1).padStart(2, "0")}</span>
                    <span>{displayTags[1]}</span>
                  </div>
                  <h3>{work.title}</h3>
                  <p>{getWorkDescription(work)}</p>

                  <div className="wrk__foot">
                    <div className="wrk__tags">
                      {displayTags.map((tag) => (
                        <span key={`${work.title}-${tag}`}>{tag}</span>
                      ))}
                    </div>
                    <a
                      href={work.projectLink || "#contact"}
                      target={external ? "_blank" : undefined}
                      rel={external ? "noreferrer" : undefined}
                      className="app__textlink wrk__open"
                    >
                      Open project
                      <FiArrowUpRight aria-hidden="true" />
                    </a>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default AppWrap(
  MotionWrap(Work, "app__works"),
  "work",
  "app__band--paper"
);
